import React, { useCallback, useState } from 'react';
import { Result } from 'antd';
import { useTranslation } from 'react-i18next';
import { hasResource } from 'utils/resourceAuth';
import { ResourceCode } from 'constants/resourceCodes';
import { OrderQuotaStrategy, OrderQuotaStrategyType } from './graphql/orderQuotaGraphql';
import CustomerTierBar from './components/CustomerTierBar';
import OrderQuotaHubList from './components/OrderQuotaHubList';
import OrderQuotaWorkspace from './components/OrderQuotaWorkspace';

interface OrderQuotaPageProps {
    strategyType: OrderQuotaStrategyType;
}

/**
 * 总部 › 产能管理。分拣产能管理、尾程产能管理均为「左侧 HUB 列表 + 右侧工作区」；
 * 分拣产能管理另在顶部放客户分级栏（分级对全部 HUB 生效）。
 */
const OrderQuotaPage: React.FC<OrderQuotaPageProps> = ({ strategyType }) => {
    const { t } = useTranslation();
    const canAccess = hasResource([ResourceCode.menu.ONBOARDING_ORDER_QUOTA]);
    const [hubId, setHubId] = useState<string | undefined>(undefined);
    const [refreshKey, setRefreshKey] = useState(0);
    const handleChanged = useCallback(() => setRefreshKey((key) => key + 1), []);
    /* 客户分级导入后递增：工作区据此重新拉取，不重挂载，保留所选周 */
    const [tierVersion, setTierVersion] = useState(0);
    const handleTierApplied = useCallback(() => {
        setTierVersion((version) => version + 1);
        handleChanged();
    }, [handleChanged]);
    const isSorting = strategyType === OrderQuotaStrategy.CUSTOMER_PICKUP_HUB;

    if (!canAccess) {
        return (
            <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
                <Result status="403" title={t('common.noPermission')} />
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg shadow-sm flex flex-col h-full w-full min-w-0 overflow-hidden">
            {isSorting && <CustomerTierBar onApplied={handleTierApplied} />}
            <div className="flex flex-1 min-h-0 min-w-0">
                <OrderQuotaHubList
                    strategyType={strategyType}
                    selectedHubId={hubId}
                    onSelect={setHubId}
                    refreshKey={refreshKey}
                />
                <div className="flex-1 min-w-0 overflow-auto p-4">
                    {hubId && (
                        <OrderQuotaWorkspace
                            strategyType={strategyType}
                            hubId={hubId}
                            reloadKey={tierVersion}
                            onChanged={handleChanged}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export const CustomerPickupHubQuotaPage: React.FC = () => <OrderQuotaPage strategyType={OrderQuotaStrategy.CUSTOMER_PICKUP_HUB} />;

export const DestWarehouseQuotaPage: React.FC = () => <OrderQuotaPage strategyType={OrderQuotaStrategy.DEST_WAREHOUSE} />;

export default OrderQuotaPage;
